import type { DocumentReference } from "fhir/r5";
import { BundlePatientSummary } from "@/services/fhir";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFhirData } from "@/utils/useFhirData";
import { rememberPatientDocSummary } from "../helpers/patientDocStatusCache";
import { isHistoricalDocument } from "../helpers/documentTransactions";
import {
  ECOMMERCE_DOCUMENT_CATEGORIES,
  type DocumentCategory,
} from "../types";

function extractDocsSplit(
  bundle: unknown,
  category: string,
): { active: DocumentReference[]; cancelled: DocumentReference[] } {
  const entries =
    useFhirData.extractPatientSummaryResource(
      bundle,
      "DocumentReference",
      category,
    )?.[0]?.entry || [];

  const active: DocumentReference[] = [];
  const cancelled: DocumentReference[] = [];

  for (const entry of entries) {
    const resource = (entry as { resource?: DocumentReference })?.resource;
    if (resource?.resourceType !== "DocumentReference") continue;
    if (isHistoricalDocument(resource)) {
      cancelled.push(resource);
    } else {
      active.push(resource);
    }
  }

  return { active, cancelled };
}

export interface LoadSupportDocumentsParams {
  cpf: string;
  organizationId?: string;
  patientKey: string;
}

export interface LoadSupportDocumentsResult {
  categories: DocumentCategory[];
  forbidden: boolean;
}

/**
 * Carrega as 4 categorias de DocumentReference de suporte do PatientSummary.
 */
export const ecommerceSupportDocumentsService = {
  async loadSupportDocuments(
    params: LoadSupportDocumentsParams,
  ): Promise<LoadSupportDocumentsResult> {
    const { cpf, organizationId, patientKey } = params;

    const bundle = await BundlePatientSummary({
      identifier: cpf,
      organizationId,
      entries: ECOMMERCE_DOCUMENT_CATEGORIES.map((category) => category.code),
    });

    if (bundle?.status === HTTPResponseStatus.Forbidden) {
      return { categories: [], forbidden: true };
    }

    const categories = ECOMMERCE_DOCUMENT_CATEGORIES.map(({ code, label }) => {
      const { active, cancelled } = extractDocsSplit(bundle, code);
      return {
        code,
        label,
        docs: active,
        cancelledDocs: cancelled,
      };
    });

    if (patientKey) {
      rememberPatientDocSummary(patientKey, categories, organizationId);
    }

    return { categories, forbidden: false };
  },
};
